import {
  KeyboardSensor,
  PointerSensor,
  SensorDescriptor,
  SensorOptions,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { WaiverType } from "features/waivers/waiverApi";
import { ReactNode, createContext, useContext, useMemo, useState } from "react";
import {
  BlockItem,
  FieldItem,
  FieldType,
  RowItem,
  TemplateBlockItem,
} from "./types";

type Direction = "up" | "down";

function uniqueId() {
  return Math.floor(Math.random() * Date.now()).toString(16);
}

function defaultField() {
  return {
    id: uniqueId(),
    type: FieldType.text,
    name: "Text Field",
  };
}

export type DraggingItem = {
  originalCoordinates: FieldCoordinates;
  field: FieldItem;
};
export type FormEditorContextType = {
  handleDragStart: (event: any) => void;
  handleDragOver: (event: any) => void;
  handleDragEnd: (event: any) => void;
  handleMoveRow: (active: any, over: any) => void;
  handleMoveBlock: (active: any, over: any) => void;
  typeIn: (types: FieldType[], coordinates: FieldCoordinates) => boolean;
  handleFieldChange: (
    coordinates: FieldCoordinates,
    item: Partial<FieldItem>
  ) => void;
  handleBlockChange: (item: Partial<BlockItem>, blockIndex: number) => void;
  handleAddBlocks: (blocks?: BlockItem[]) => void;
  handleAddRows: (blockIndex: number, rows?: RowItem[]) => void;
  handleAddFields: (
    blockIndex?: number,
    rowIndex?: number,
    fields?: FieldItem[]
  ) => void;
  handleDeleteRow: (coordinates: RowCoordinates) => void;
  handleDeleteBlock: (blockIndex: number) => void;
  handleDeleteField: (coordinates: FieldCoordinates) => void;
  handleAddTemplate: (
    template?: TemplateBlockItem,
    blockIndex?: number,
    rowIndex?: number
  ) => void;
  getField: (coordinates: FieldCoordinates) => FieldItem | null;
  getRow: (coordinates: RowCoordinates) => RowItem | null;
  getBlock: (blockIndex: number) => BlockItem | null;
  sensors: SensorDescriptor<SensorOptions>[];
  value?: BlockItem[];
  onChange?: (value?: any) => void;
  label?: string;
  handleMoveRowByDir: (
    blockIndex: number,
    rowIndex: number,
    direction: Direction
  ) => void;
  handleMoveBlockByDir: (blockIndex: number, direction: Direction) => void;
  type?: WaiverType;
  draggingItem?: DraggingItem;
  rowIsFull: (row: RowItem | null) => boolean;
};

export const FormEditorContext = createContext<FormEditorContextType>(
  {} as FormEditorContextType
);

export interface FormEditorDataProviderProps {
  value?: BlockItem[];
  onChange?: (value?: any) => void;
  label?: string;
  children: ReactNode;
  type?: WaiverType;
}
export type FieldCoordinates = {
  blockIndex: number;
  rowIndex: number;
  fieldIndex: number;
};

export type RowCoordinates = {
  blockIndex: number;
  rowIndex: number;
};

export default function FormEditorDataProvider({
  value,
  onChange,
  children,
  label,
  type,
}: FormEditorDataProviderProps) {
  const [draggingItem, setDraggingItem] = useState<DraggingItem>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const context = useMemo(() => {
    function handleMoveBlockByDir(blockIndex: number, direction: Direction) {
      if (value && value[blockIndex]) {
        const newIndex = direction === "up" ? blockIndex - 1 : blockIndex + 1;
        if (newIndex >= 0 && newIndex < value.length) {
          const newValue = arrayMove(value, blockIndex, newIndex);
          onChange && onChange({ target: { value: newValue } });
        }
      }
    }
    function handleMoveRowByDir(
      blockIndex: number,
      rowIndex: number,
      direction: Direction
    ) {
      if (value && value[blockIndex] && value[blockIndex].rows[rowIndex]) {
        const newIndex = direction === "up" ? rowIndex - 1 : rowIndex + 1;
        if (newIndex >= 0 && newIndex < value[blockIndex].rows.length) {
          let newValue: BlockItem[] = value
            ? JSON.parse(JSON.stringify(value))
            : [];
          newValue[blockIndex].rows = arrayMove(
            value[blockIndex].rows,
            rowIndex,
            newIndex
          );
          onChange && onChange({ target: { value: newValue } });
        }
      }
    }

    function handleMoveBlock(active: any, over: any) {
      if (value) {
        const oldIndex = value.findIndex(r => r.id === active.id);
        const newIndex = value.findIndex(r => r.id === over.id);
        const newValue = arrayMove(value, oldIndex, newIndex);
        onChange && onChange({ target: { value: newValue } });
      }
    }
    function handleMoveRow(active: any, over: any, blockIndex?: number) {
      if (value && (blockIndex || blockIndex === 0)) {
        const oldIndex = value[blockIndex].rows.findIndex(
          r => r.id === active.id
        );
        const newIndex = value[blockIndex].rows.findIndex(
          r => r.id === over.id
        );
        let newValue: BlockItem[] = value
          ? JSON.parse(JSON.stringify(value))
          : [];
        newValue[blockIndex].rows = arrayMove(
          value[blockIndex].rows,
          oldIndex,
          newIndex
        );
        onChange && onChange({ target: { value: newValue } });
      }
    }

    async function handleDragEndOld(event: any) {
      const { active, over } = event;

      console.log({ active, over });
      if (active?.id === over?.id) {
        //event.preventDefault();
        return;
      }

      if (active?.data?.current?.type === "block") {
        handleMoveBlock(active, over);
      }
      if (active?.data?.current?.type === "row") {
        handleMoveRow(active, over, active?.data?.current?.blockIndex);
      }
    }

    function handleDragStart(event: any) {
      const { active } = event;
      //console.log({ active });
      if (value) {
        setDraggingItem(active.data.current.draggingItem);
      }
    }

    function handleDragOver(event: any) {
      const { active, over, draggingRect } = event;
      console.log({ active, over });
      const activeItem: DraggingItem = active?.data?.current?.draggingItem;
      const overItem: DraggingItem = over?.data?.current?.draggingItem;
      if (!activeItem || !overItem) {
        return;
      }
      /*let newIndex;
      if (overId in prev) {
        // We're at the root droppable of a container
        newIndex = overItem.length + 1;
      } else {
        const isBelowLastItem =
          over &&
          overIndex === overItems.length - 1 &&
          draggingRect.offsetTop > over.rect.offsetTop + over.rect.height;

        const modifier = isBelowLastItem ? 1 : 0;

        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      }*/

      const activeContainer = getRow(
        active?.data?.current?.draggingItem.originalCoordinates
      );
      const overContainer = getRow(
        over?.data?.current?.draggingItem.originalCoordinates
      );

      if (!activeContainer || !overContainer) {
        return;
      }
      /*console.log({
        activeContainerId: activeContainer.id,
        overContainerId: overContainer.id,
      });*/
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];

      let overFields = overItem
        ? newValue[overItem.originalCoordinates.blockIndex].rows[
            overItem.originalCoordinates.rowIndex
          ].fields
        : [];
      let activeFields = activeItem
        ? newValue[activeItem.originalCoordinates.blockIndex].rows[
            activeItem.originalCoordinates.rowIndex
          ].fields
        : [];

      if (overFields && activeFields) {
        const overIds = overFields.map(f => f.id);
        const activeIds = activeFields.map(f => f.id);

        /*console.log({
          activeId: activeItem?.field.id,
          overId: overItem?.field.id,
          overIds,
          activeIds,
        });*/
        if (activeItem && overIds.includes(activeItem?.field?.id)) {
          //Move element in container

          newValue[overItem.originalCoordinates.blockIndex].rows[
            overItem.originalCoordinates.rowIndex
          ].fields = arrayMove(
            overFields,
            activeItem.originalCoordinates.fieldIndex,
            overItem.originalCoordinates.fieldIndex
          );
          onChange && onChange({ target: { value: newValue } });
        } else {
          const newFields = [
            ...overFields.slice(0, overItem.originalCoordinates.fieldIndex),
            activeItem.field,
            ...overFields.slice(overItem.originalCoordinates.fieldIndex),
          ] as FieldItem[];
          newValue[overItem.originalCoordinates.blockIndex].rows[
            overItem.originalCoordinates.rowIndex
          ].fields = newFields;

          if (activeIds.includes(activeItem?.field?.id)) {
            activeFields.splice(activeItem.originalCoordinates.fieldIndex, 1);
            newValue[activeItem.originalCoordinates.blockIndex].rows[
              activeItem.originalCoordinates.rowIndex
            ].fields = activeFields;
          }

          onChange && onChange({ target: { value: newValue } });
        }
      }
    }

    function handleDragEnd(event: any) {
      const { active, over } = event;
      /*console.log({ type: "handleDragEnd", active, over });

      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];
      let oldFields =
        newValue[active.data.current.blockIndex].rows[
          active.data.current.rowIndex
        ].fields;

      newValue[active.data.current.blockIndex].rows[
        active.data.current.rowIndex
      ].fields = [];*/
      setDraggingItem(undefined);
    }

    /*function handleDragStart(event: any) {
    setActiveItem(
      value?.find(i => JSON.stringify(i) === JSON.stringify(event.active))
    );
  }*/

    function getField(coordinates: FieldCoordinates): FieldItem | null {
      const field = value
        ? value[coordinates.blockIndex].rows[coordinates.rowIndex].fields[
            coordinates.fieldIndex
          ]
        : null;
      return field;
    }

    function getRow(coordinates: RowCoordinates): RowItem | null {
      const row = value
        ? value[coordinates.blockIndex].rows[coordinates.rowIndex]
        : null;
      return row;
    }

    function rowIsFull(row: RowItem | null): boolean {
      let hasFullWidthField = false;
      row?.fields?.forEach(field => {
        if (field.full_width) {
          hasFullWidthField = true;
        }
      });

      if (hasFullWidthField) {
        return true;
      }
      if (row?.fields && row?.fields.length >= 3) {
        return true;
      }
      return false;
    }

    function getBlock(blockIndex: number): BlockItem | null {
      return value && value[blockIndex] ? value[blockIndex] : null;
    }

    function typeIn(types: FieldType[], coordinates: FieldCoordinates) {
      const field = getField(coordinates);
      return field ? types.includes(field.type) : false;
    }

    function handleFieldChange(
      coordinates: FieldCoordinates,
      item: Partial<FieldItem>
    ) {
      const field = getField(coordinates);
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];

      if (field) {
        newValue[coordinates?.blockIndex].rows[coordinates?.rowIndex].fields[
          coordinates?.fieldIndex
        ] = {
          ...field,
          ...item,
        };
        onChange && onChange({ target: { value: newValue } });
      }
    }

    function handleBlockChange(item: Partial<BlockItem>, blockIndex: number) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];
      newValue[blockIndex] = { ...newValue[blockIndex], ...item };
      onChange && onChange({ target: { value: newValue } });
    }

    function handleAddTemplate(
      template?: TemplateBlockItem,
      blockIndex?: number,
      rowIndex?: number
    ) {
      if (
        (blockIndex || blockIndex === 0) &&
        (rowIndex || rowIndex === 0) &&
        template?.rows[0] &&
        template?.rows[0].fields[0]
      ) {
        handleAddFields(blockIndex, rowIndex, [
          { ...template?.rows[0].fields[0], id: uniqueId() },
        ]);
        return;
      }
      if (
        (blockIndex || blockIndex === 0) &&
        template?.rows[0] &&
        template?.rows[0].fields[0]
      ) {
        handleAddRows(blockIndex, [
          {
            ...template?.rows[0],
            id: uniqueId(),
            fields: template?.rows[0].fields.map(f => ({
              ...f,
              id: uniqueId(),
            })),
          },
        ]);
        return;
      }

      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];

      if (template?.rows) {
        let rows: RowItem[] = [];
        template.rows.forEach(row => {
          let fields: FieldItem[] = [];
          row.fields.forEach(field => {
            fields.push({ ...field, id: uniqueId() });
          });
          rows.push({ id: uniqueId(), fields });
        });
        newValue.push({ name: template.name, id: uniqueId(), rows });

        onChange && onChange({ target: { value: newValue } });
      }
    }
    function handleAddBlocks(blocks?: BlockItem[]) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];

      const newBlockIndex = newValue.length;
      if (blocks) {
        blocks.forEach(block => {
          newValue.push(block);
        });
      } else {
        newValue.push({
          id: uniqueId(),
          name: "Block",
          rows: [{ id: uniqueId(), fields: [] }],
        });
      }

      onChange && onChange({ target: { value: newValue } });
    }

    function handleAddRows(blockIndex: number, rows?: RowItem[]) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];

      const newBlockIndex = newValue.length;
      if (rows) {
        rows.forEach(row => {
          newValue[blockIndex].rows.push(
            row || { id: uniqueId(), fields: [defaultField()] }
          );
        });
      } else {
        newValue[blockIndex].rows.push({
          id: uniqueId(),
          fields: [defaultField()],
        });
      }
      onChange && onChange({ target: { value: newValue } });
    }

    function handleAddFields(
      blockIndex?: number,
      rowIndex?: number,
      fields?: FieldItem[]
    ) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];
      if ((!blockIndex && blockIndex !== 0) || !newValue[blockIndex as any]) {
        handleAddBlocks([
          {
            id: uniqueId(),
            name: "Block",
            rows: [{ id: uniqueId(), fields: fields || [] }],
          },
        ]);
        return;
      }
      if ((!rowIndex && rowIndex !== 0) || !newValue[rowIndex as any]) {
        handleAddRows(
          blockIndex,
          fields ? [{ id: uniqueId(), fields }] : undefined
        );
        return;
      }

      if (fields) {
        fields.forEach(field => {
          newValue[blockIndex as any].rows[rowIndex as any].fields.push(
            field || defaultField
          );
        });
      } else {
        newValue[blockIndex as any].rows[rowIndex as any].fields.push(
          defaultField()
        );
      }

      onChange && onChange({ target: { value: newValue } });
    }

    function handleDeleteBlock(blockIndex: number) {
      let newValue = value ? JSON.parse(JSON.stringify(value)) : [];
      newValue.splice(blockIndex, 1);
      onChange && onChange({ target: { value: newValue } });
    }

    function handleDeleteRow(coordinates: RowCoordinates) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];
      newValue[coordinates.blockIndex].rows.splice(coordinates.rowIndex, 1);

      onChange && onChange({ target: { value: newValue } });
    }

    function handleDeleteField(coordinates: FieldCoordinates) {
      let newValue: BlockItem[] = value
        ? JSON.parse(JSON.stringify(value))
        : [];
      if (
        newValue[coordinates.blockIndex] &&
        newValue[coordinates.blockIndex].rows[coordinates.rowIndex]
      ) {
        newValue[coordinates.blockIndex].rows[
          coordinates.rowIndex
        ].fields.splice(coordinates.fieldIndex, 1);
        onChange && onChange({ target: { value: newValue } });
      }
    }

    return {
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      //handleMoveItem,
      handleMoveBlock,
      handleMoveRow,
      typeIn,
      handleFieldChange,
      handleBlockChange,
      handleAddBlocks,
      handleAddRows,
      handleAddFields,
      handleDeleteRow,
      handleDeleteField,
      handleAddTemplate,
      sensors,
      value,
      onChange,
      label,
      getField,
      getRow,
      getBlock,
      handleDeleteBlock,
      handleMoveBlockByDir,
      handleMoveRowByDir,
      type,
      draggingItem,
      rowIsFull,
    };
  }, [draggingItem, value, onChange, label]);

  return (
    <FormEditorContext.Provider value={context}>
      {children}
    </FormEditorContext.Provider>
  );
}

export const useFormEditor = () => useContext(FormEditorContext);
